import Logger from '@/logger';
import { type OmeggaPlayer } from '@/plugin';
import { steamcmdCheckUpdate, steamcmdDownloadGame } from '@/updater';
import { readBinaryVersion } from '@omegga/matchers/version';
import Omegga from '@omegga/server';
import { type IOmeggaOptions } from '@omegga/types';
import { sanitize } from '@util/chat';
import { checkWsl } from '@util/wsl';
import { serverEvents } from '@webserver/backend/events';
import readline from 'readline';
import { install, update as updatePlugins } from './plugin';

declare global {
  interface String {
    brightRed: string;
    brightGreen: string;
    brightYellow: string;
    brightBlue: string;
    brightMagenta: string;
    brightCyan: string;
    brightWhite: string;
  }
}

let log: (...args: unknown[]) => void,
  err: (...args: unknown[]) => void,
  warn: (...args: unknown[]) => void,
  info: (...args: unknown[]) => void;

type TerminalCommand = {
  aliases: string[];
  desc: string;
  descLines?: () => string[];
  fn(this: Terminal, ...args: string[]): void | Promise<void>;
};

function gameCommand(
  aliases: string[],
  desc: string,
  usage: string,
  command: string,
  quoteArgs?: boolean,
): TerminalCommand {
  return {
    aliases,
    desc,
    fn(...args) {
      if (!this.omegga.started) {
        err('The server is not running');
        return;
      }
      if (!args.length) {
        err('usage:', ('/' + aliases[0] + ' ' + usage).yellow);
        return;
      }
      this.omegga.writeln(
        `${command} ${quoteArgs ? '"' : ''}${args.join(' ')}${
          quoteArgs ? '"' : ''
        }`,
      );
      this.rl.prompt(true);
    },
  };
}

// subcommands of /user, rendered in /help and in the /user usage list
const USER_COMMANDS = [
  {
    command: '/user passwd <username>',
    desc: "reset a user's password",
  },
  {
    command: '/user rename <username> <newname>',
    desc: 'rename a user',
  },
  {
    command: '/user resetmfa <username>',
    desc: 'clear TOTP, passkeys, and recovery codes',
  },
  {
    command: '/user disable <username>',
    desc: 'prevent a user from signing in',
  },
  {
    command: '/user enable <username>',
    desc: 're-enable a disabled user',
  },
  {
    command: '/user delete <username>',
    desc: 'permanently delete a user',
  },
  {
    command: '/user grantowner <username>',
    desc: 'make a user an owner',
  },
  {
    command: '/user revokeowner <username>',
    desc: 'remove ownership from a user',
  },
];

const COMMANDS: TerminalCommand[] = [
  // Brickadia commands
  gameCommand(
    ['kick'],
    'kick a player',
    '<name|id> [reason]',
    'Chat.Command /Kick',
  ),
  gameCommand(
    ['ban', 'banish'],
    'ban a player',
    '<name|id> [time] [reason]',
    'Ban',
  ),
  gameCommand(
    ['unban', 'pardon'],
    'unban a player',
    '<name|id>',
    'Chat.Command /Unban',
  ),
  gameCommand(
    ['grantrole', 'giverole'],
    'grant a role to a player',
    '<role> <name|id>',
    'Chat.Command /GrantRole',
  ),
  gameCommand(
    ['revokerole', 'takerole'],
    'revoke a role from a player',
    '<role> <name|id>',
    'Chat.Command /RevokeRole',
  ),
  gameCommand(
    ['clearbricks'],
    "clear a player's bricks",
    '<name|id>',
    'Bricks.Clear',
    true,
  ),
  gameCommand(['clearallbricks'], 'clear all bricks', '', 'Bricks.ClearAll'),

  // Help command
  {
    aliases: ['help', 'cmds', 'commands'],
    desc: 'list supported commands and their descriptions',
    fn() {
      const maxLen = Math.max(...COMMANDS.map(c => c.aliases[0].length));
      log('Omegga Help Text:\n');
      log(
        '  Console input not starting with / will be sent in chat from a "SERVER" user',
      );
      log(
        '  Console input starting with / will be treated as one of the following commands\n',
      );
      log(
        '-- Available Omegga commands (type',
        '/command'.yellow.underline,
        'to run)',
      );

      for (const command of COMMANDS.sort((a, b) =>
        a.aliases[0].localeCompare(b.aliases[0]),
      )) {
        const name = command.aliases[0];
        this.log(
          '  ',
          ('/' + name).yellow.underline,
          '-'.padStart(maxLen - name.length + 1),
          command.desc,
        );
        if (command.descLines)
          for (const descLine of command.descLines())
            this.log('    ', ' '.repeat(maxLen + 1), descLine);
      }
    },
  },

  // Server controls
  {
    aliases: ['stop', 'exit', 'quit', 'close'],
    desc: 'stop the server and close Omegga',
    fn() {
      return this.shutdown();
    },
  },
  {
    aliases: ['restart'],
    desc: 'trigger a server restart (without announcement or saving)',
    async fn() {
      log('Restarting server...');
      await this.omegga.restartServer();
    },
  },
  {
    aliases: ['version', 'ver'],
    desc: 'print the game version',
    fn() {
      // the running version, and what's actually on disk right now - a steam
      // update can replace the binary underneath a running server
      const running = this.omegga.version;
      const binary = readBinaryVersion(this.omegga.getGameBinaryPath());

      if (running > 0) log('Brickadia Server', ('CL' + running).green);
      else warn('Game version is not known yet.');

      if (binary == null) return;

      if (running > 0 && binary !== running) {
        warn(
          'Server binary on disk is',
          ('CL' + binary).yellow + '.',
          'Restart to run the updated build.',
        );
      } else if (!(running > 0)) {
        log('Server binary on disk is', ('CL' + binary).green);
      }
    },
  },
  {
    aliases: ['update'],
    desc: 'try to update the server and restart if started (without announcement or saving',
    async fn() {
      if (!this.omegga.config.__STEAM) {
        err(
          'This command is only available when the server is installed via SteamCMD',
        );
        return;
      }
      if (this.omegga.stopping || this.omegga.starting) {
        err(
          'The server is currently starting or stopping. Please wait a moment',
        );
        return;
      }
      const wasStarted = this.omegga.started;
      if (wasStarted) {
        log('Stopping server to update...');
        await this.omegga.stop();
      }
      log('Updating server...');
      try {
        await steamcmdDownloadGame({
          steambeta: this.omegga.config.server?.steambeta,
          steambetaPassword: this.omegga.config.server?.steambetaPassword,
        });
        log('Server updated successfully.');
      } catch (e) {
        err('Error updating server:', e);
      }
      if (wasStarted) {
        log('Starting server...');
        await this.omegga.start();
      }
    },
  },
  {
    aliases: ['updatecheck', 'uc', 'check'],
    desc: 'check for updates. /uc show for details, /uc on|off to toggle auto-update',
    async fn(subcommand?: string) {
      if (!this.omegga.config.__STEAM) {
        err(
          'This command is only available when the server is installed via SteamCMD',
        );
        return;
      }

      const db = this.omegga.webserver?.database;

      if (subcommand === 'on' || subcommand === 'off') {
        if (!db) {
          err('Database not available.');
          return;
        }
        const config = await db.getAutoRestartConfig();
        config.autoUpdateEnabled = subcommand === 'on';
        await db.setAutoRestartConfig(config);
        log(
          subcommand === 'on'
            ? 'Auto-update enabled.'.green
            : 'Auto-update disabled.'.yellow,
        );
        return;
      }

      log('Checking for updates...');
      const result = await steamcmdCheckUpdate(
        this.omegga.config.server?.steambeta,
      );
      if (!result) {
        err('Failed to check for updates.');
        return;
      }
      const { local, remote, hasUpdate } = result;

      if (hasUpdate === true) log('Update available!'.green);
      else if (hasUpdate === false) log('Up to date.'.green);
      else log('Could not determine update status.'.yellow);

      if (subcommand === 'show') {
        const branch =
          local.betaKey && local.betaKey !== 'main' ? local.betaKey : 'public';
        const remoteBuild = remote?.branches?.[branch];
        const remotePart = remoteBuild
          ? `BuildID ${remoteBuild.buildid}` +
            (remoteBuild.timeupdated
              ? ` (${new Date(Number(remoteBuild.timeupdated) * 1000).toLocaleString()})`
              : '')
          : 'unknown (private branch?)';
        log(
          [
            `  Local:  BuildID ${local.buildId} (${local.installState})`,
            `  Remote: ${remotePart}`,
            `  Branch: ${local.betaKey ?? 'public'}`,
          ].join('\n'),
        );
      }

      if (db) {
        const config = await db.getAutoRestartConfig();
        log(
          config.autoUpdateEnabled
            ? `Auto-update: enabled (every ${config.autoUpdateIntervalMins}m)`
            : 'Auto-update: disabled',
        );
      }
    },
  },
  {
    aliases: ['kill'],
    desc: 'forcefully kill brickadia server process without closing omegga',
    async fn() {
      log('Stopping server...');
      await this.omegga.stop();
    },
  },
  {
    aliases: ['start'],
    desc: 'start the server if it is stopped',
    fn() {
      if (
        !this.omegga ||
        (this.omegga && (this.omegga.starting || this.omegga.started))
      ) {
        err('The server is already running');
        return;
      }

      log('Starting server...');
      this.omegga.start();
    },
  },
  {
    aliases: ['cmd'],
    desc: 'run a console command on the Brickadia server. requires /debug for log to show',
    fn(...args) {
      if (!this.omegga.started) {
        err('The server is not running');
        return;
      }
      this.omegga.writeln(args.join(' '));
    },
  },
  {
    aliases: ['debug'],
    desc: 'toggle visibility of brickadia console logs',
    fn() {
      this.options.debug = !this.options.debug;
      log(
        'Brickadia logs now',
        this.options.debug ? 'visible'.green : 'hidden'.red,
      );
    },
  },
  {
    aliases: ['status'],
    desc: 'display server status information. brick count, online players, etc',
    async fn() {
      if (!this.omegga.started) {
        err('Omegga is not running');
        return;
      }
      const msToTime = (ms: number) => new Date(ms).toISOString().slice(11, 19);
      try {
        const status = await this.omegga.getServerStatus();

        log('Server Status');
        this.log(`
${status.serverName.yellow}
Bricks: ${(status.bricks + '').yellow}
Components: ${(status.components + '').yellow}
Uptime: ${msToTime(status.time).yellow}
Players: ${status.players.length === 0 ? 'none'.grey : ''}
  ${status.players
    .map(p => `[${msToTime(p.time).grey}] ${p.name.brightYellow}`)
    .join('\n  ')}
`);
      } catch {
        err('An error occurred while getting server status');
      }
    },
  },

  // plugin stuff
  {
    aliases: ['reload'],
    desc: 'shorthand for /plugins reload',
    async fn() {
      await COMMANDS.find(c => c.aliases.includes('plugins'))?.fn.bind(this)(
        'reload',
      );
    },
  },
  {
    aliases: ['plugins', 'plugin', 'p'],
    desc: 'manage Omegga plugins',
    descLines: () => [
      '/plugins install <...plugin names>'.yellow + ' installs plugins',
      '/plugins update [...plugin names]'.yellow +
        ' updates all plugins, or those specified',
      '/plugins load <...plugin names>'.yellow +
        ' loads plugins, and enables if they were disabled',
      '/plugins unload <...plugin names>'.yellow + ' unloads plugins',
      '/plugins reload [...plugin names]'.yellow +
        ' reloads all plugins, or those specified',
      '/plugins enable <...plugin names>'.yellow + ' enables plugins',
      '/plugins disable <...plugin names>'.yellow +
        ' disables plugins, and unloads if they were loaded',
    ],
    async fn(subcommand: string, ...args: string[]) {
      if (!this.omegga.pluginLoader) {
        err('Omegga is not using plugins');
        return;
      }

      if (!subcommand) {
        const plugins = this.omegga.pluginLoader.plugins;
        if (plugins.length === 0) {
          log('No plugins are installed');
          log('  Install some with', '/plugins install'.yellow.underline);
        } else {
          log('Installed plugins'.yellow.underline);
          for (const plugin of plugins) {
            const name = plugin.getName();
            const docs = plugin.getDocumentation();
            if (plugin.isLoaded()) log(name.cyan.underline + ' (loaded)');
            else if (plugin.isEnabled())
              log(name.green.underline + ' (enabled, not loaded)');
            else log(name.red.underline + ' (disabled)');

            log('  ' + (docs?.description ?? 'no description'));
            log(('  Author: ' + (docs?.author ?? 'unknown')).gray);
          }
        }
      } else if (subcommand === 'install') {
        // install plugins
        await install(args, { verbose: true });
        log();
        log(
          'Use',
          '/plugins reload'.yellow,
          'to reload all plugins and use the installed ones',
        );
      } else if (subcommand === 'update') {
        // update plugins
        await updatePlugins(args, { verbose: true });
        log();
        log(
          'Use',
          '/plugins reload'.yellow,
          'to reload all plugins and use the updated ones',
        );
      } else if (subcommand === 'load') {
        if (args.length === 0) {
          log('Please specify at least one plugin to load');
          return;
        }

        for (const pluginName of args) {
          const plugin = this.omegga.pluginLoader.plugins.find(
            p => p.getName().toLowerCase() === pluginName.toLowerCase(),
          );

          if (!plugin) {
            warn('No plugin by the name', pluginName.cyan);
            continue;
          }

          const name = plugin.getName();
          if (plugin.isLoaded()) {
            warn(
              name.cyan,
              'is already loaded, reload with',
              ('/plugins reload ' + name).yellow,
            );
            return;
          }

          if (!plugin.isEnabled()) {
            log('Enabling and loading', name.cyan.underline);
            plugin.setEnabled(true);
            await plugin.load();
          } else {
            log('Loading', name.cyan.underline);
            await plugin.load();
          }
        }
      } else if (subcommand === 'unload') {
        if (args.length === 0) {
          log('Please specify at least one plugin to unload');
          return;
        }

        for (const pluginName of args) {
          const plugin = this.omegga.pluginLoader.plugins.find(
            p => p.getName().toLowerCase() === pluginName.toLowerCase(),
          );

          if (!plugin) {
            warn('No plugin by the name', pluginName.cyan);
            continue;
          }

          const name = plugin.getName();
          if (!plugin.isLoaded()) {
            warn(
              name.cyan,
              'is already unloaded, load with',
              ('/plugins load ' + name).yellow,
            );
            return;
          }

          log('Unloading', name.cyan.underline);
          await plugin.unload();
        }
      } else if (subcommand === 'reload') {
        let plugins = (
          args.length === 0
            ? this.omegga.pluginLoader.plugins
            : this.omegga.pluginLoader.plugins.filter(p =>
                args
                  .map(a => a.toLowerCase())
                  .includes(p.getName().toLowerCase()),
              )
        ).filter(p => p.isEnabled());

        const loaded = plugins.filter(p => p.isLoaded());
        log('Unloading', loaded.length, 'plugins');
        for (const plugin of loaded) await plugin.unload();

        if (args.length === 0) {
          log('Scanning for new plugins');
          if (!(await this.omegga.pluginLoader.scan())) {
            err('Could not scan for plugins. All plugins are unloaded');
            return;
          }
        }

        plugins = (
          args.length === 0
            ? this.omegga.pluginLoader.plugins
            : this.omegga.pluginLoader.plugins.filter(p =>
                args
                  .map(a => a.toLowerCase())
                  .includes(p.getName().toLowerCase()),
              )
        ).filter(p => p.isEnabled());
        log('Loading', plugins.length, 'plugins');
        for (const plugin of plugins) await plugin.load();

        log('Reloaded', plugins.length, 'plugins');
      } else if (subcommand === 'enable') {
        if (args.length === 0) {
          log('Please specify at least one plugin to enable');
          return;
        }

        for (const pluginName of args) {
          const plugin = this.omegga.pluginLoader.plugins.find(
            p => p.getName().toLowerCase() === pluginName.toLowerCase(),
          );

          if (!plugin) {
            warn('No plugin by the name', pluginName.cyan);
            continue;
          }

          const name = plugin.getName();
          if (plugin.isEnabled()) {
            warn(
              name.cyan,
              'is already enabled, disable with',
              ('/plugins disable ' + name).yellow,
            );
            return;
          }

          log(
            'Enabling',
            name.cyan.underline,
            '(load with',
            ('/plugins load ' + name).yellow + ')',
          );
          plugin.setEnabled(true);
        }
      } else if (subcommand === 'disable') {
        if (args.length === 0) {
          log('Please specify at least one plugin to disable');
          return;
        }

        for (const pluginName of args) {
          const plugin = this.omegga.pluginLoader.plugins.find(
            p => p.getName().toLowerCase() === pluginName.toLowerCase(),
          );

          if (!plugin) {
            warn('No plugin by the name', pluginName.cyan);
            continue;
          }

          const name = plugin.getName();
          if (!plugin.isEnabled()) {
            warn(
              name.cyan,
              'is already disabled, enable with',
              ('/plugins enable ' + name).yellow,
            );
            return;
          }

          if (plugin.isLoaded()) {
            log('Unloading and disabling', name.cyan.underline);
            await plugin.unload();
            plugin.setEnabled(false);
          } else {
            log('Disabling', name.cyan.underline);
            plugin.setEnabled(false);
          }
        }
      }
    },
  },
  {
    aliases: ['world', 'worlds', 'w'],
    desc: 'Manage worlds on the server',
    descLines: () => ['Type /worlds load a world'],
    async fn(subcommand: string, ...args: string[]) {
      switch (subcommand) {
        default: {
          const commands = [
            {
              command: '/worlds use [world]',
              desc: 'set or remove a world as the default world for startup',
              short: '/w u [world]',
            },
            {
              command: '/worlds load <world> [rev]',
              desc: 'load a world',
              short: '/w load <world> [rev]',
            },
            {
              command: '/worlds saveas <world>',
              desc: 'save the current world as a new world',
              short: '/w sa <world>',
            },
            {
              command: '/worlds save',
              desc: 'save the current world',
              short: '/w s',
            },
            {
              command: '/worlds revisions <world>',
              desc: 'list revisions of a world',
              short: '/w r <world>',
            },
            {
              command: '/worlds new',
              desc: 'create a new world',
              short: '/w new <name> [Plate|Space|Studio|Peaks]',
            },
          ];
          log(
            'Default world is',
            this.omegga.getNextWorld()?.file?.yellow || 'none'.grey,
          );
          log('Available world commands:');
          for (const { command, desc, short } of commands) {
            this.log('  ', command.yellow, '-', desc, `(${short.yellow})`);
          }
          return;
        }

        case 'use':
        case 'u': {
          const world = args[0]?.replace(/\.brdb$/, '');
          if (world && !this.omegga.worldExists(world)) {
            err(`World "${world}" does not exist`);
            return;
          }
          if (this.omegga.setActiveWorld(world || null)) {
            if (world) {
              log(
                `Default world set to ${world.yellow}. It will be loaded next startup.`,
              );
            } else {
              log(
                'Default world cleared. No world will be loaded on next startup.',
              );
            }
          } else {
            err(`Failed to set default world to ${world.yellow}.`);
          }
          return;
        }

        case 'list':
        case 'ls': {
          // list worlds
          const worlds = this.omegga.getWorlds();
          if (worlds.length === 0) {
            log(
              'No worlds found. Create one with',
              '/worlds saveas <world>'.yellow,
            );
          } else {
            log('Available worlds:');
            const prefix = this.omegga.worldPath + '/';
            for (const world of worlds) {
              this.log(
                '  ',
                world.replace(prefix, '').replace(/\.brdb$/, '').yellow,
              );
            }
          }
          return;
        }
        case 'load':
        case 'l': {
          if (!this.omegga.started) {
            err('The server is not running');
            return;
          }

          // load a world
          if (args.length === 0) {
            err('Please specify a world to load');
            return;
          }
          const worldName = args[0].replace(/\.brdb$/, '');
          if (!this.omegga.getWorldPath(worldName)) {
            err(`World "${worldName}" does not exist`);
            return;
          }
          let revision: number | undefined;
          if (args.length > 1) {
            revision = parseInt(args[1], 10);
            if (isNaN(revision) || revision < 1) {
              err(`Invalid revision number: ${args[1]}`);
              return;
            }
          }
          let loadRes = false;
          if (revision) {
            log(`Loading world ${worldName.yellow} at revision ${revision}...`);
            loadRes = await this.omegga.loadWorldRevision(worldName, revision);
          } else {
            log(`Loading world ${worldName.yellow}...`);
            loadRes = await this.omegga.loadWorld(worldName);
          }
          if (loadRes) {
            log(`World ${worldName.yellow} loaded successfully.`);
          } else {
            err(`Failed to load world ${worldName.yellow}`);
          }

          return;
        }
        case 'saveas':
        case 'sa': {
          if (!this.omegga.started) {
            err('The server is not running');
            return;
          }

          // save the current world as a new world
          if (args.length === 0) {
            err('Please specify a world to save as');
            return;
          }
          const saveWorldName = args[0].replace(/\.brdb$/, '');
          if (this.omegga.getWorldPath(saveWorldName)) {
            err(`World "${saveWorldName}" already exists`);
            return;
          }
          log(`Saving current world as ${saveWorldName.yellow}...`);
          const saveAsRes = await this.omegga.saveWorldAs(saveWorldName);
          if (saveAsRes) {
            log(`Current world saved as ${saveWorldName.yellow} successfully.`);
          } else {
            err(`Failed to save current world as ${saveWorldName.yellow}`);
          }
          return;
        }

        case 'save':
        case 's': {
          if (!this.omegga.started) {
            err('The server is not running');
            return;
          }

          // save the current world
          log('Saving current world...');
          const res = await this.omegga.saveWorld();
          if (res) {
            log('Current world saved successfully.');
          } else {
            err('Failed to save the current world');
          }
          return;
        }

        case 'revisions':
        case 'r': {
          if (!this.omegga.started) {
            err('The server is not running');
            return;
          }

          // list revisions of a world
          if (args.length === 0) {
            err('Please specify a world to list revisions for');
            return;
          }
          const revisionsWorldName = args[0].replace(/\.brdb$/, '');
          if (!this.omegga.getWorldPath(revisionsWorldName)) {
            err(`World "${revisionsWorldName}" does not exist`);
            return;
          }
          log(`Listing revisions for world ${revisionsWorldName.yellow}...`);
          try {
            const revisions =
              await this.omegga.getWorldRevisions(revisionsWorldName);
            if (revisions.length === 0) {
              log('No revisions found for world', revisionsWorldName.yellow);
            } else {
              log('Revisions for world', revisionsWorldName.yellow + ':');
              for (const rev of revisions) {
                this.log(
                  '  ',
                  `Revision ${rev.index.toString().yellow} - ${
                    rev.date
                      .toISOString()
                      .replace('T', ' ')
                      .replace('.000Z', '').blue
                  }`,
                  `(${rev.note})`.grey,
                );
              }
            }
          } catch (e) {
            err('An error occurred while getting world revisions:', String(e));
          }
          return;
        }

        case 'new':
        case 'n': {
          if (!this.omegga.started) {
            err('The server is not running');
            return;
          }

          // create a new world
          if (args.length === 0) {
            err('Please specify a name for the new world');
            return;
          }
          const newWorldName = args[0].replace(/\.brdb$/, '');
          if (this.omegga.getWorldPath(newWorldName)) {
            err(`World "${newWorldName}" already exists`);
            return;
          }
          const map = (args[1] ? args[1] : 'Plate') as
            | 'Plate'
            | 'Space'
            | 'Studio'
            | 'Peaks';
          if (!['Plate', 'Space', 'Studio', 'Peaks'].includes(map)) {
            err(`Invalid preset "${map}". Valid map are: Plate, Space, Studio`);
            return;
          }
          log(`Creating new world ${newWorldName.yellow}...`);
          this.omegga.createEmptyWorld(newWorldName, map);
        }
      }
    },
  },
  {
    aliases: ['user'],
    desc: 'manage web UI users',
    descLines: () =>
      USER_COMMANDS.map(({ command, desc }) => command.yellow + ' - ' + desc),
    async fn(subcommand?: string, username?: string, newName?: string) {
      const db = this.omegga.webserver?.database;
      if (!db) {
        err('Database not available.');
        return;
      }
      const known = USER_COMMANDS.map(c => c.command.split(' ')[1]);
      if (!subcommand || !known.includes(subcommand)) {
        if (subcommand) err(`Unknown subcommand "${subcommand}".`);
        log('Available user commands:');
        for (const { command, desc } of USER_COMMANDS) {
          this.log('  ', command.yellow, '-', desc);
        }
        return;
      }
      if (!username) {
        err(
          `Usage: /user ${subcommand} <username>${
            subcommand === 'rename' ? ' <newname>' : ''
          }`,
        );
        return;
      }
      if (!(await db.userExists(username))) {
        err(`User "${username}" does not exist.`);
        return;
      }
      switch (subcommand) {
        case 'passwd': {
          const password = await new Promise<string>(resolve =>
            this.rl.question('New password: ', resolve),
          );
          if (!password) {
            err('Password cannot be empty.');
            return;
          }
          await db.userPasswd(username, password);
          log(`Password updated for "${username.yellow}".`);
          break;
        }
        case 'resetmfa': {
          await db.resetUserMfa(username);
          log(
            `MFA cleared for "${username.yellow}" (TOTP, passkeys, recovery codes).`,
          );
          break;
        }
        case 'rename': {
          if (!newName) {
            err('Usage: /user rename <username> <newname>');
            return;
          }
          const user = await db.findUserByUsername(username);
          if (!user) {
            err(`User "${username}" does not exist.`);
            return;
          }
          try {
            await db.renameUser(user.id, newName);
          } catch (e) {
            err(e instanceof Error ? e.message : 'Error renaming user.');
            return;
          }
          log(`Renamed "${username.yellow}" to "${newName.yellow}".`);
          break;
        }
        case 'disable': {
          await db.banUser(username, true);
          log(`Disabled "${username.yellow}". They can no longer sign in.`);
          break;
        }
        case 'enable': {
          await db.banUser(username, false);
          log(`Enabled "${username.yellow}". They can sign in again.`);
          break;
        }
        case 'delete': {
          const confirm = await new Promise<string>(resolve =>
            this.rl.question(
              `Permanently delete "${username}"? This cannot be undone. (y/N): `,
              resolve,
            ),
          );
          if (!/^y(es)?$/i.test(confirm.trim())) {
            log('Cancelled.');
            return;
          }
          await db.deleteUser(username);
          log(`Deleted user "${username.yellow}".`);
          break;
        }
        case 'grantowner': {
          try {
            await db.grantOwner(username);
          } catch (e) {
            err(e instanceof Error ? e.message : 'Error granting ownership.');
            return;
          }
          log(`Granted ownership to "${username.yellow}".`);
          break;
        }
        case 'revokeowner': {
          try {
            await db.revokeOwner(username);
          } catch (e) {
            err(e instanceof Error ? e.message : 'Error revoking ownership.');
            return;
          }
          log(`Revoked ownership from "${username.yellow}".`);
          break;
        }
      }
    },
  },
];

// the terminal wraps omegga and displays console output and handles console input
export default class Terminal {
  options: IOmeggaOptions;
  omegga: Omegga;
  rl: readline.Interface;
  // readline closes when stdin ends (ctrl+c in a tty, a detached container) -
  // using it after that throws ERR_USE_AFTER_CLOSE
  #rlClosed = false;
  #shuttingDown = false;
  constructor(omegga: Omegga, options: IOmeggaOptions = {}) {
    this.options = options;
    this.omegga = omegga;

    // terminal interface
    this.rl = readline.createInterface({
      input: process.stdin,
      output: process.stdout,
      terminal: true,
    });
    this.rl.setPrompt('> '.brightGreen);

    // shortand fns
    log = (...args) => this.log('>>'.green, ...args);
    err = (...args) => this.error('!>'.red, ...args);
    info = (...args) => this.log('?>'.blue, ...args);
    warn = (...args) => this.warn('W>'.yellow, ...args);

    // print log line if debug is enabled
    let launcherDone = false;
    let lastDoneMessage = '';
    omegga.on('line', l => {
      // Redact the hosting token only in what we print to the debug log.
      // Brickadia echoes its full command line (including -Token="...") via a
      // LogInit line on stdout; restrict the redaction to engine log lines
      // (LogInit/etc.) so we don't mangle other output (e.g. chat) that might
      // legitimately contain a -Token= string. The raw `l` is still delivered
      // to matchers/plugins elsewhere, so this must not mutate that value.
      if (options.debug)
        this.log(
          '::'.blue,
          /^Log\w+:/.test(l)
            ? l.replace(/-Token="[^"]*"/, '-Token="REDACTED"')
            : l,
        );
      else if (!launcherDone) {
        if (l.match(/Update Failed/)) {
          err(l);
          info(
            'This might be resolved by deleting brickadia and reinstalling:',
          );
          info('  rm -rf ~/.local/share/brickadia-launcher'.grey);
        }
        if (l.startsWith('DOWNLOADING')) {
          // TODO: if someone is proactive enough in the future, you could turn these steps into a legitimate progress bar
          /* Here's some launcher log lines that might be helpful
            Grabbing manifest and checking game files...
            DOWNLOADING MANIFEST | Hold on...
            DOWNLOADING MANIFEST (100%) | Hold on...
            DOWNLOADING MANIFEST (100%) | 0 B/s - 13 KiB / 13 KiB (Unknown time remaining...)
            DOWNLOADING MANIFEST (100%) | Done!
            Checking launcher and downloading version info...
            Installing patches and booting...
            DOWNLOADING BRICKADIA (0%) | Done!
            DOWNLOADING BRICKADIA (100%) | 110 MiB/s - 584 MiB / 584 MiB (1 second remaining...)
            DOWNLOADING BRICKADIA (100%) | Nearly done...
            DOWNLOADING BRICKADIA (100%), INSTALLING BRICKADIA (86%) | Nearly done...
          */
          const isDone = Boolean(l.match(/Done!/)) && l !== lastDoneMessage;
          if (process.stdout.isTTY) {
            process.stdout.clearLine(0);
            process.stdout.cursorTo(0);
            process.stdout.write(l);
            // add a newline on "Done!" but prevent newlines on repeat done messages
            if (isDone) process.stdout.write('\n');
          } else if (isDone) {
            // without a tty there is no cursor to rewind, so print the
            // milestones on their own lines instead of every progress update
            process.stdout.write(l + '\n');
          }
          if (isDone) lastDoneMessage = l;
        } else if (l.match(/Disabling core dumps./)) {
          launcherDone = true;
        }
      }

      // [2025.07.07-20.45.40:310][845]LogBRWorldManager: World successfully loaded.
      if (
        l.match(
          /^\[[^\]]+\]\[[^\]]+\]LogBRWorldManager: World successfully loaded\.$/,
        )
      ) {
        info('World successfully loaded.');
      }
    });

    // print debug events regardless of debug status
    omegga.on('debug', l => this.log('?>'.magenta, l));

    // print chat events as players join/leave the server
    omegga.on('join', (p: OmeggaPlayer) =>
      this.log(
        `${p.displayName.underline} (${p.name.underline}) joined.`.brightBlue,
      ),
    );
    omegga.on('leave', (p: OmeggaPlayer) =>
      this.log(
        `${p.displayName.underline} (${p.name.underline}) left.`.brightBlue,
      ),
    );
    // admin actions belong in the console, which is also what logs them
    omegga.on('kick', (name: string, kicker: string, reason: string) =>
      this.log(
        `${name.underline} was kicked by ${kicker.underline} (${reason})`
          .brightRed,
      ),
    );
    omegga.on(
      'ban',
      (name: string, kicker: string, reason: string, duration?: string) =>
        this.log(
          `${name.underline} was banned by ${kicker.underline} (${reason})${
            duration ? `, expires in ${duration}` : ''
          }`.brightRed,
        ),
    );
    omegga.on('chat', (name, message) => {
      const player = omegga.getPlayer(name);
      this.log(
        `${(player?.displayName ?? player?.name ?? name).brightYellow.underline}: ${message}`,
      );
    });
    omegga.on('start', () => {
      const wsl = checkWsl();
      log(
        `Server has started${
          wsl === 1 ? ' in single thread mode due to WSL1' : ''
        }. Type ${'/help'.yellow} for more commands`,
      );
      if (!this.omegga.getNextWorld()) {
        info(
          'No',
          'default world'.yellow,
          'is configured. Run',
          '/worlds use <world>'.yellow,
          'to load one on startup.',
        );
      }

      // check if this is WSL2 and wsl2binds is installed
      if (
        wsl === 2 &&
        this.omegga.pluginLoader &&
        !this.omegga.pluginLoader.plugins.some(p => p.getName() === 'wsl2binds')
      ) {
        warn(
          '####'.yellow,
          `You are running ${'WSL2'.yellow} and you don't have the ${
            'wsl2binds'.yellow
          } plugin.`,
        );
        warn(
          '####'.yellow,
          `The wsl2binds plugin works as a ${
            'UDP proxy'.underline
          } between Windows and the WSL2 VM.`,
        );
        warn('####'.yellow, 'Install it by:');
        warn('####'.yellow, ' 1. Closing omegga with', '/stop'.green);
        warn(
          '####'.yellow,
          ' 2. Installing wsl2binds with',
          'omegga install gh:Meshiest/wsl2binds'.green,
        );
        warn('####'.yellow, ' 3. Restarting', 'omegga'.green);
      }
    });
    omegga.on('mapchange', ({ map }) =>
      log('Map changed to', (map.charAt(0).toUpperCase() + map.slice(1)).green),
    );
    omegga.on('unauthorized', () => {
      err('Server failed authentication check');
      info('You can clear auth tokens with', 'omegga auth -gl'.green);
      info(
        'This will require you to paste a new hosting token or sign-in again',
      );
      process.exit();
    });
    omegga.on('error', e => err('Server caught unhandled exception:\n' + e));
    omegga.on('server:stopped', () =>
      log('Server has closed. Type', '/stop'.yellow, 'to close omegga'),
    );

    this.rl.on('line', this.handleLine.bind(this));
    this.rl.on('close', () => (this.#rlClosed = true));

    // Gracefully shut down server on Ctrl+C and SIGTERM. Listening for SIGINT on
    // the interface stops readline from closing itself on ctrl+c in a tty -
    // every log after that close would throw while trying to redraw the prompt
    this.rl.on('SIGINT', () => this.shutdown());
    process.on('SIGINT', () => this.shutdown());
    process.on('SIGTERM', () => this.shutdown());
  }

  // stop the server and close omegga - a second request force closes
  async shutdown() {
    if (this.#shuttingDown) {
      err('Force closing Omegga...');
      process.exit(1);
    }
    this.#shuttingDown = true;
    log('Stopping server...');
    try {
      await this.omegga.stop();
    } catch (e) {
      err('Error stopping server:', e);
    }
    this.rl.close();
    // file only, pairing the `Hello!` main.ts records at startup
    Logger.record('log', ['>>'.green, 'Goodbye']);
    process.exit();
  }

  async handleLine(line: string) {
    // file only: readline echoed it already, and output with no sign of the
    // input that caused it is hard to read
    if (line.trim()) Logger.record('log', ['$'.grey, line]);

    if (line.startsWith('/')) {
      const [cmd, ...args] = line.slice(1).split(' ');
      const c = COMMANDS.find(c => c.aliases.includes(cmd));
      if (!c) {
        err(
          `unrecognized command /${cmd.underline}. Type /help for more info`
            .red,
        );
      } else {
        try {
          const res = c.fn.bind(this)(...args);
          if (res instanceof Promise) await res;
        } catch (e) {
          err('unhandled terminal error', e);
        }
      }
    } else if (line.trim().length > 0) {
      if (this.omegga.started) {
        // broadcast when the chat does not start with a command
        this.omegga.broadcast(
          `"[<b><color=\\"ff00ff\\">SERVER</></>]: ${sanitize(line)}"`,
        );
        if (process.stdout.isTTY) process.stdout.clearLine(0);
        this.log(`[${'SERVER'.brightMagenta.underline}]: ${line}`);

        // if omegga is running a webserver - send this message in the chat log
        if (this.omegga.webserver) {
          const user = { name: 'SERVER', id: '', web: true, color: 'ff00ff' };
          // create database entry, send to web ui
          const chatLog = await this.omegga.webserver.database.addChatLog(
            'msg',
            user,
            line,
          );
          serverEvents.emit('chat', chatLog);
        }
      } else {
        err(
          'Server is not started yet. type'.red,
          '/help'.yellow,
          'for more info'.red,
        );
      }
    }
  }

  // let readline render a log without interrupting user input
  #write(method: 'log' | 'debug' | 'warn' | 'error', args: unknown[]) {
    // the log file's only sink for terminal output, including the constructor
    // events that never pass through Logger. recorded before stdout so a closed
    // pipe cannot cost the line, and from raw args because the file times it
    Logger.record(method, args);
    // stdout has no cursor controls when omegga is piped or run in a container
    if (process.stdout.isTTY) {
      process.stdout.clearLine(0);
      process.stdout.cursorTo(0);
    }
    console[method](...Logger.timestamped(args));
    // there is no prompt to redraw without a terminal, and its escape codes
    // would just be noise in a piped log
    if (process.stdout.isTTY && !this.#rlClosed) this.rl.prompt(true);
  }

  log(...args: unknown[]) {
    this.#write('log', args);
  }

  // let readline render a debug log without interrupting user input
  debug(...args: unknown[]) {
    this.#write('debug', args);
  }

  // let readline render a warning log without interrupting user input
  warn(...args: unknown[]) {
    this.#write('warn', args);
  }

  // let readline render an error log without interrupting user input
  error(...args: unknown[]) {
    this.#write('error', args);
  }
}
